import { first, forEach, includes, isArray, isEmpty, isObject, isBoolean, kebabCase } from 'lodash';

import { UrlService, WindowProxy } from '@sm360/widget-commons-core-services';
import { getFilters } from 'inventory-listing/services/ListingUsedService/ListingUsedService.js';

class UrlManagement {
    changeUrl(listingUsed) {
        const windowProxy = WindowProxy.get();

        if (!listingUsed || !windowProxy) {
            return;
        }

        const url = this.buildUrl(listingUsed);
        const queryParams = this.createQueryParams(listingUsed);
        if (url) {
            windowProxy.history.replaceState(
                {
                    filter: 'filter',
                },
                'filter',
                `${url}${queryParams}`
            );
        }
    }

    createQueryParams(listingUsed) {
        let queryParamsString = '';
        const queryParams = UrlService.getUrlParameters();

        if (!listingUsed) {
            return queryParamsString;
        }
        listingUsed.vehicleCriteria.forEach((value, key) => {
            if (
                !includes(key, 'colander') &&
                !includes(key, 'model') &&
                !includes(key, 'category') &&
                !includes(key, 'catalogMakeIds') &&
                !includes(key, 'catalogModelIds') &&
                !includes(key, 'vehicleStatuses')
            ) {
                if (isArray(value)) {
                    queryParams[key] = value.join(',');
                } else if (isObject(value)) {
                    const formattedKey = key.split('Range')[0];
                    queryParams[`${formattedKey}From`] = isEmpty(value) ? '' : value.from;
                    queryParams[`${formattedKey}To`] = isEmpty(value) ? '' : value.to;
                } else if (!isObject(value) && !isBoolean(value)) {
                    queryParams[key] = value;
                }
            }
        });
        forEach(queryParams, (paramValue, paramName) => {
            const paramSign = !queryParamsString ? '?' : '&';
            const isCatalogId =
                !!(paramName === 'catalogMakeId' || paramName === 'catalogModelId') &&
                !!(listingUsed.vehicleCriteria.makeIds.length > 0 || listingUsed.vehicleCriteria.modelIds.length > 0);
            if (paramValue && !isCatalogId) {
                queryParamsString += `${paramSign}${paramName}=${paramValue}`;
            }
        });

        return queryParamsString;
    }

    buildUrl(listingUsed) {
        if (!listingUsed) {
            return null;
        }
        const { vehicleCriteria, colanderGroup } = listingUsed;
        const filters = listingUsed.metadatas;
        const makes = getFilters(filters, 'makeId');
        let url = listingUsed.contextUrl;
        const { colanderSlug } = vehicleCriteria;

        const modelId = first(vehicleCriteria.modelIds);

        const singleMake = makes.length === 1 && listingUsed.makePriorities[0];

        if (colanderSlug) {
            const selectedColander = listingUsed.colanders.find((colander) => colander.slug === colanderSlug);
            if (!selectedColander)
                throw new Error(
                    `Cannot edit URL because the active colander (${colanderSlug}) cannot be found in the XMS website configuration: the '${colanderGroup}' hub should contain the '${colanderSlug}' colander.`
                );

            const selectedColanderUrl = selectedColander.urlMapping;
            url = UrlService.replaceParamWithValue(url, '{hubCategory}', selectedColanderUrl);
        } else {
            url = UrlService.replaceParamWithValue(url, '{hubCategory}', 'used');
        }

        if (vehicleCriteria.makeIds.length === 1) {
            const makeId = parseInt(vehicleCriteria.makeIds[0], 10);
            const selectedMakeUrl = makes.find((make) => {
                return make.code === makeId;
            })?.url;
            url = UrlService.replaceParamWithValue(url, '{make}', selectedMakeUrl);
        } else if (singleMake && modelId) {
            url = UrlService.replaceParamWithValue(url, '{make}', kebabCase(singleMake?.name));
        } else {
            url = UrlService.replaceParamWithValue(url, '/{make}', '');
        }

        if (modelId) {
            const models = getFilters(filters, 'modelId');
            let selectedMakeUrl = models.find((model) => {
                return model.code === modelId;
            })?.url;

            // STWEBSITES-108 if landrover and discovery model, remove the land_rover_ prefix from the model in the url
            if (url.includes('land_rover') || url.includes('land-rover')) {
                const modelName = selectedMakeUrl.toLowerCase();
                if (/discovery/i.test(modelName)) {
                    selectedMakeUrl = modelName.replace(/^land_rover_/i, '');
                }
            }
            url = UrlService.replaceParamWithValue(url, '{model}', selectedMakeUrl);
        } else {
            url = UrlService.replaceParamWithValue(url, '/{model}', '');
        }

        return UrlService.replaceParamWithValue(url, '/{year}', '');
    }
}

export default new UrlManagement();
